import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { getSectorColor } from "@/lib/sectorColors";

interface CorrelationResult {
  tickers: string[];
  matrix: number[][];
  period: string;
  dataPoints: number;
  skippedTickers: string[];
}

interface Cluster {
  id: number;
  members: string[];
  avgIntraCorr: number;
}

// Agglomerative clustering using average-linkage on distance = 1 - correlation
function buildClusters(tickers: string[], matrix: number[][], threshold: number): Cluster[] {
  const n = tickers.length;
  let clusters: number[][] = tickers.map((_, i) => [i]);

  const dist = (a: number[], b: number[]) => {
    let sum = 0;
    for (const i of a) {
      for (const j of b) {
        sum += 1 - matrix[i][j];
      }
    }
    return sum / (a.length * b.length);
  };

  while (clusters.length > 1) {
    let minDist = Infinity;
    let mergeI = 0;
    let mergeJ = 1;
    for (let i = 0; i < clusters.length; i++) {
      for (let j = i + 1; j < clusters.length; j++) {
        const d = dist(clusters[i], clusters[j]);
        if (d < minDist) {
          minDist = d;
          mergeI = i;
          mergeJ = j;
        }
      }
    }
    if (minDist > 1 - threshold) break;
    const merged = [...clusters[mergeI], ...clusters[mergeJ]];
    clusters = clusters.filter((_, idx) => idx !== mergeI && idx !== mergeJ);
    clusters.push(merged);
  }

  return clusters.map((members, id) => {
    let sum = 0;
    let count = 0;
    for (let i = 0; i < members.length; i++) {
      for (let j = i + 1; j < members.length; j++) {
        sum += matrix[members[i]][members[j]];
        count++;
      }
    }
    return {
      id,
      members: members.map((i) => tickers[i]),
      avgIntraCorr: count > 0 ? sum / count : 1,
    };
  }).sort((a, b) => b.members.length - a.members.length);
}

const CLUSTER_COLORS = [
  "hsl(220, 70%, 55%)",
  "hsl(0, 70%, 55%)",
  "hsl(140, 60%, 45%)",
  "hsl(35, 80%, 50%)",
  "hsl(280, 60%, 55%)",
  "hsl(175, 60%, 40%)",
  "hsl(330, 65%, 50%)",
  "hsl(60, 70%, 45%)",
  "hsl(200, 50%, 50%)",
  "hsl(15, 75%, 50%)",
];

const CLUSTER_BG_COLORS = [
  "hsl(220, 70%, 95%)",
  "hsl(0, 70%, 95%)",
  "hsl(140, 60%, 93%)",
  "hsl(35, 80%, 93%)",
  "hsl(280, 60%, 93%)",
  "hsl(175, 60%, 93%)",
  "hsl(330, 65%, 93%)",
  "hsl(60, 70%, 93%)",
  "hsl(200, 50%, 93%)",
  "hsl(15, 75%, 93%)",
];

interface Props {
  result: CorrelationResult;
  sectorMap?: Record<string, string>;
}

export function CorrelationClusterView({ result, sectorMap }: Props) {
  const [threshold, setThreshold] = useState([0.5]);

  const clusters = useMemo(() => {
    return buildClusters(result.tickers, result.matrix, threshold[0]);
  }, [result, threshold]);

  // Compute inter-cluster correlations
  const interCluster = useMemo(() => {
    if (clusters.length < 2) return [];
    const links: { from: number; to: number; corr: number }[] = [];
    for (let a = 0; a < clusters.length; a++) {
      for (let b = a + 1; b < clusters.length; b++) {
        let sum = 0;
        let count = 0;
        for (const ta of clusters[a].members) {
          for (const tb of clusters[b].members) {
            const ai = result.tickers.indexOf(ta);
            const bi = result.tickers.indexOf(tb);
            if (ai >= 0 && bi >= 0) {
              sum += result.matrix[ai][bi];
              count++;
            }
          }
        }
        if (count > 0) links.push({ from: a, to: b, corr: sum / count });
      }
    }
    return links.sort((a, b) => Math.abs(b.corr) - Math.abs(a.corr));
  }, [clusters, result]);

  const singletons = clusters.filter((c) => c.members.length === 1);
  const groups = clusters.filter((c) => c.members.length > 1);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          Cluster Analysis
          <Badge variant="secondary" className="text-xs font-normal">
            {clusters.length} clusters · {result.tickers.length} tickers
          </Badge>
        </CardTitle>
        <CardDescription>
          Positions grouped by correlation similarity using agglomerative clustering
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Threshold slider */}
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground whitespace-nowrap">Cluster Threshold:</span>
          <Slider
            value={threshold}
            onValueChange={setThreshold}
            min={0.2}
            max={0.9}
            step={0.05}
            className="w-64"
          />
          <Badge variant="outline" className="text-xs font-mono">
            ρ ≥ {threshold[0].toFixed(2)}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {threshold[0] >= 0.7 ? "Tight clusters" : threshold[0] <= 0.35 ? "Loose clusters" : "Moderate grouping"}
          </span>
        </div>

        {/* Cluster groups */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map((cluster, idx) => (
            <Card
              key={cluster.id}
              className="border-2 transition-shadow hover:shadow-md"
              style={{ borderColor: CLUSTER_COLORS[idx % CLUSTER_COLORS.length] }}
            >
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: CLUSTER_COLORS[idx % CLUSTER_COLORS.length] }}
                    />
                    <span className="text-sm font-semibold">Cluster {idx + 1}</span>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    Avg ρ: {cluster.avgIntraCorr.toFixed(2)}
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {cluster.members.map((ticker) => (
                    <Tooltip key={ticker}>
                      <TooltipTrigger asChild>
                        <Badge
                          className="text-xs font-mono cursor-default"
                          style={{
                            backgroundColor: CLUSTER_BG_COLORS[idx % CLUSTER_BG_COLORS.length],
                            color: CLUSTER_COLORS[idx % CLUSTER_COLORS.length],
                            borderColor: CLUSTER_COLORS[idx % CLUSTER_COLORS.length],
                          }}
                          variant="outline"
                        >
                          {ticker}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent className="text-xs">
                        <p className="font-semibold">{ticker}</p>
                        {sectorMap?.[ticker] && <p className="text-muted-foreground">{sectorMap[ticker]}</p>}
                        <p>In cluster with {cluster.members.length - 1} other position{cluster.members.length > 2 ? "s" : ""}</p>
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </div>
                {/* Sector composition for this cluster */}
                {sectorMap && (() => {
                  const sectorCounts: Record<string, number> = {};
                  cluster.members.forEach(t => {
                    const s = sectorMap[t] || "Unknown";
                    sectorCounts[s] = (sectorCounts[s] || 0) + 1;
                  });
                  const entries = Object.entries(sectorCounts).sort((a, b) => b[1] - a[1]);
                  return (
                    <div className="flex flex-wrap gap-1">
                      {entries.map(([sector, count]) => {
                        const sc = getSectorColor(sector === "Unknown" ? undefined : sector, false);
                        return (
                          <span key={sector} className="text-[10px] flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: sc.stroke }} />
                            {sector} ({count})
                          </span>
                        );
                      })}
                    </div>
                  );
                })()}
                {cluster.members.length > 1 && (
                  <p className="text-xs text-muted-foreground">
                    {cluster.avgIntraCorr >= 0.7
                      ? "⚠️ Highly correlated — consider reducing overlap"
                      : cluster.avgIntraCorr >= 0.5
                        ? "Moderately correlated group"
                        : "Loosely correlated group"}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Singletons */}
        {singletons.length > 0 && (
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-2">
              Unclustered Positions ({singletons.length})
            </p>
            <div className="flex flex-wrap gap-1.5">
              {singletons.map((c) => (
                <Badge key={c.members[0]} variant="outline" className="text-xs font-mono">
                  {c.members[0]}
                </Badge>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              ✅ These positions are independent — good for diversification
            </p>
          </div>
        )}

        {/* Inter-cluster links */}
        {interCluster.length > 0 && (
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-2">Inter-Cluster Relationships</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {interCluster.slice(0, 6).map((link, i) => {
                const fromIdx = groups.findIndex((g) => g.id === clusters[link.from]?.id);
                const toIdx = groups.findIndex((g) => g.id === clusters[link.to]?.id);
                const fromLabel = fromIdx >= 0 ? `Cluster ${fromIdx + 1}` : clusters[link.from]?.members[0] || "?";
                const toLabel = toIdx >= 0 ? `Cluster ${toIdx + 1}` : clusters[link.to]?.members[0] || "?";
                return (
                  <div key={i} className="flex items-center justify-between bg-muted/50 rounded-lg px-3 py-2 text-xs">
                    <span className="font-medium">{fromLabel} ↔ {toLabel}</span>
                    <Badge
                      variant="outline"
                      className="text-xs font-mono"
                      style={{
                        color: link.corr >= 0.3 ? "hsl(0, 70%, 50%)" : link.corr <= -0.1 ? "hsl(220, 70%, 50%)" : undefined,
                      }}
                    >
                      {link.corr >= 0 ? "+" : ""}{link.corr.toFixed(2)}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Sector Breakdown Statistics */}
        {sectorMap && Object.keys(sectorMap).length > 0 && (() => {
          const sectorTickers: Record<string, string[]> = {};
          result.tickers.forEach(t => {
            const s = sectorMap[t] || "Unknown";
            if (!sectorTickers[s]) sectorTickers[s] = [];
            sectorTickers[s].push(t);
          });
          const entries = Object.entries(sectorTickers).sort((a, b) => b[1].length - a[1].length);
          const total = result.tickers.length;

          return (
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-3">Sector Breakdown</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {entries.map(([sector, tickers]) => {
                  const sc = getSectorColor(sector === "Unknown" ? undefined : sector, false);
                  const pct = ((tickers.length / total) * 100).toFixed(0);
                  // Find which clusters these tickers belong to
                  const clusterIds = new Set<number>();
                  tickers.forEach(t => {
                    groups.forEach((g, i) => { if (g.members.includes(t)) clusterIds.add(i + 1); });
                  });
                  return (
                    <div key={sector} className="flex items-start gap-3 bg-muted/50 rounded-lg px-3 py-2">
                      <div className="w-3 h-3 rounded-full mt-0.5 shrink-0" style={{ backgroundColor: sc.stroke }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium truncate">{sector}</span>
                          <span className="text-xs text-muted-foreground font-mono">{tickers.length} ({pct}%)</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-1.5 mt-1">
                          <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, backgroundColor: sc.stroke }} />
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {tickers.map(t => (
                            <span key={t} className="text-[10px] font-mono text-muted-foreground">{t}</span>
                          ))}
                        </div>
                        {clusterIds.size > 0 && (
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            Spread across {clusterIds.size === 1 ? `Cluster ${[...clusterIds][0]}` : `${clusterIds.size} clusters`}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {entries.length > 1 && (
                <p className="text-xs text-muted-foreground mt-2">
                  {entries[0][1].length / total >= 0.5
                    ? `⚠️ ${entries[0][0]} dominates at ${((entries[0][1].length / total) * 100).toFixed(0)}% — consider diversifying`
                    : `✅ ${entries.length} sectors represented — reasonable diversification`}
                </p>
              )}
            </div>
          );
        })()}
      </CardContent>
    </Card>
  );
}
